import * as THREE from 'three';
import type { UnitDef } from '../../../gameplay/types';
import { ModelBuilder, Part, type PartOpts } from './builder';

const SKIN: Record<string, number> = {
  english: 0xe8bc9c, french: 0xe4b896, germans: 0xeac0a0, spanish: 0xd8a880, portuguese: 0xd4a47c, ottomans: 0xc8966e,
  japanese: 0xe0b48e, chinese: 0xdcae86, mongols: 0xc89870, indians: 0xa0704a, aztecs: 0xa87650, egyptians: 0xb08058,
};
export const civSkin = (civ: string): number => SKIN[civ] ?? 0xd8a880;
const STEEL = 0xa8aeb8;
const WOOD = 0x6a4a2a;
const LEATHER = 0x5a3a22;
const HORSE = [0x5a3a22, 0x3a2a1a, 0x8a6a4a, 0xd8d0c0];

const T = (color: number, team = 0, part: Part = Part.Static, pivot?: [number, number, number]): PartOpts => ({ color, team, part, pivot });
/** A shade of the player's colour (1 itself, lower darker, above 1 lighter) for soldiers' clothing. */
const TS = (shade: number, part: Part = Part.Static, pivot?: [number, number, number]): PartOpts => T(0xffffff, shade, part, pivot);

/** Shades of the player's colour that soldiers dress in: every cloth part is one of these. */
const CLOTH = { coat: 1, sleeve: 0.82, trim: 0.66, trousers: 0.5, cap: 0.7, darkHat: 0.55, lightCloth: 1.45 };

export interface UnitStyle {
  /** Civ uniform accent colour. */
  accent: number;
  skin: number;
}

function addWeapon(b: ModelBuilder, u: UnitDef, x: number, y: number, z: number, pivot: [number, number, number]): void {
  const arm = (c: number) => T(c, 0, Part.ArmR, pivot);
  switch (u.weapon) {
    case 'sword':
      b.box(0.02, 0.26, 0.035, arm(STEEL), x, y + 0.02, z + 0.1, 0.9, 0, 0);
      b.box(0.08, 0.015, 0.03, arm(0xb08a3a), x, y - 0.04, z + 0.04, 0.9, 0, 0);
      break;
    case 'spear':
      b.cyl(0.012, 0.012, 0.85, 5, arm(WOOD), x, y + 0.05, z + 0.12, 0.25, 0, 0);
      b.cone(0.03, 0.09, 5, arm(STEEL), x, y + 0.48, z + 0.24, 0.25, 0, 0);
      break;
    case 'axe':
      b.cyl(0.012, 0.012, 0.3, 5, arm(WOOD), x, y, z + 0.06, 0.9, 0, 0);
      b.box(0.015, 0.07, 0.08, arm(STEEL), x, y + 0.06, z + 0.17, 0.9, 0, 0);
      break;
    case 'hammer':
      // Short-hafted sledge: wooden handle, iron head across the end.
      b.cyl(0.013, 0.013, 0.3, 5, arm(WOOD), x, y, z + 0.06, 0.9, 0, 0);
      b.box(0.11, 0.05, 0.05, arm(0x4a4a50), x, y + 0.06, z + 0.18, 0.9, 0, 0);
      break;
    case 'club':
      b.cyl(0.03, 0.015, 0.3, 6, arm(0x4a3a2a), x, y, z + 0.1, 0.9, 0, 0);
      b.box(0.012, 0.05, 0.2, arm(0x222226), x + 0.03, y + 0.03, z + 0.12, 0.9, 0, 0);
      break;
    case 'bow': {
      const g = new THREE.TorusGeometry(0.2, 0.012, 4, 10, Math.PI * 0.9);
      b.add(g, T(WOOD, 0, Part.ArmL, [-0.12, 0.46, 0]), -0.15, 0.4, 0.14, 0, Math.PI / 2, Math.PI / 2 + 0.05 * Math.PI);
      break;
    }
    case 'crossbow':
      b.box(0.04, 0.04, 0.32, arm(WOOD), x - 0.04, y + 0.05, z + 0.12);
      b.box(0.3, 0.02, 0.03, arm(0x3a3a3a), x - 0.04, y + 0.06, z + 0.26);
      break;
    case 'musket':
      b.box(0.03, 0.03, 0.62, arm(WOOD), x - 0.04, y + 0.06, z + 0.16);
      b.cyl(0.012, 0.012, 0.4, 5, arm(0x2a2a2e), x - 0.04, y + 0.08, z + 0.33, Math.PI / 2, 0, 0);
      break;
    default:
      break;
  }
}

function addHelmet(b: ModelBuilder, u: UnitDef, y: number): void {
  switch (u.helmet) {
    case 'cap':
      b.sphere(0.07, TS(CLOTH.cap), 0, y + 0.02, 0, 1, 0.6, 1, 1);
      break;
    case 'kettle':
      b.sphere(0.072, T(STEEL), 0, y + 0.025, 0, 1, 0.6, 1, 1);
      b.cyl(0.11, 0.11, 0.012, 10, T(STEEL), 0, y + 0.0, 0);
      break;
    case 'great':
      b.cyl(0.072, 0.075, 0.14, 8, T(STEEL), 0, y, 0);
      b.box(0.1, 0.01, 0.02, T(0x222222), 0, y + 0.01, 0.068);
      b.box(0.015, 0.06, 0.04, T(0, 1), 0, y + 0.1, 0);
      break;
    case 'kabuto':
      b.sphere(0.074, T(0x2a2a30), 0, y + 0.025, 0, 1, 0.65, 1, 1);
      b.cyl(0.13, 0.13, 0.01, 10, T(0x2a2a30), 0, y - 0.01, -0.02, 0.2, 0, 0);
      b.box(0.1, 0.06, 0.01, T(0xd0a020), 0, y + 0.08, 0.05, -0.4, 0, 0);
      break;
    case 'turban':
      b.sphere(0.085, TS(CLOTH.lightCloth), 0, y + 0.04, 0, 1, 0.9, 1, 1);
      b.cyl(0.02, 0.03, 0.12, 6, TS(CLOTH.lightCloth), 0, y + 0.13, -0.02);
      break;
    case 'feather':
      b.sphere(0.072, TS(CLOTH.cap), 0, y + 0.02, 0, 1, 0.6, 1, 1);
      for (let i = -1; i <= 1; i++) b.box(0.02, 0.16, 0.01, T(i === 0 ? 0xd03020 : 0x30a060, 0), i * 0.035, y + 0.1, -0.04, -0.3, 0, i * 0.3);
      break;
    case 'tricorn':
      b.cyl(0.11, 0.11, 0.025, 3, TS(CLOTH.darkHat), 0, y + 0.03, 0);
      b.sphere(0.065, TS(CLOTH.darkHat), 0, y + 0.05, 0, 1, 0.6, 1, 1);
      break;
    default:
      // Cloth hood: a villager's brown, a soldier's in a shade of the player's colour.
      b.sphere(0.07, u.model === 'villager' ? T(0x8a7a5a) : TS(CLOTH.cap), 0, y + 0.025, -0.01, 1, 0.7, 1, 1);
  }
}

/** Standing humanoid (or rider when mounted = true). Faces +Z. */
function humanoid(b: ModelBuilder, u: UnitDef, style: UnitStyle, baseY: number, mounted: boolean): void {
  const villager = u.model === 'villager';
  // Villagers wear their own browns; everyone else is dressed in shades of the player's colour.
  const cloth = (villagerColor: number, shade: number, part: Part = Part.Static, pivot?: [number, number, number]) =>
    villager ? T(villagerColor, 0, part, pivot) : TS(shade, part, pivot);
  const tunic = 0x8a6a42;
  const trousers = 0x5a4a32;
  if (!mounted) {
    b.box(0.075, 0.27, 0.085, cloth(trousers, CLOTH.trousers, Part.LegL, [-0.05, baseY + 0.27, 0]), -0.05, baseY + 0.135, 0);
    b.box(0.075, 0.27, 0.085, cloth(trousers, CLOTH.trousers, Part.LegR, [0.05, baseY + 0.27, 0]), 0.05, baseY + 0.135, 0);
    b.box(0.08, 0.04, 0.11, T(LEATHER, 0, Part.LegL, [-0.05, baseY + 0.27, 0]), -0.05, baseY + 0.02, 0.015);
    b.box(0.08, 0.04, 0.11, T(LEATHER, 0, Part.LegR, [0.05, baseY + 0.27, 0]), 0.05, baseY + 0.02, 0.015);
  } else {
    b.box(0.07, 0.07, 0.2, cloth(trousers, CLOTH.trousers), -0.11, baseY + 0.02, 0.04, 0, 0, 0.4);
    b.box(0.07, 0.07, 0.2, cloth(trousers, CLOTH.trousers), 0.11, baseY + 0.02, 0.04, 0, 0, -0.4);
  }
  const ty = baseY + (mounted ? 0.12 : 0.38);
  // Torso: the coat in the player's colour, with a darker yoke and front panel.
  b.box(0.22, 0.24, 0.13, villager ? T(tunic) : TS(CLOTH.coat), 0, ty, 0);
  b.box(0.225, 0.04, 0.135, cloth(tunic, CLOTH.trim), 0, ty + 0.1, 0.001);
  b.box(0.06, 0.24, 0.135, cloth(tunic, CLOTH.trim), 0, ty, 0.002);
  b.box(0.23, 0.03, 0.14, T(LEATHER), 0, ty - 0.09, 0);
  // Villagers wear their own browns with a sash in the player's colour, shoulder to hip, front and back.
  if (villager) b.box(0.08, 0.34, 0.15, TS(CLOTH.coat), 0, ty + 0.01, 0, 0, 0, 0.72);
  if (!villager && (u.classes & (1 << 16)) !== 0) {
    b.box(0.2, 0.12, 0.14, T(STEEL), 0, ty + 0.06, 0.003);
    b.box(0.08, 0.05, 0.08, T(STEEL), 0.13, ty + 0.1, 0.0);
    b.box(0.08, 0.05, 0.08, T(STEEL), -0.13, ty + 0.1, 0.0);
  }
  // Head
  const hy = ty + 0.2;
  b.sphere(0.062, T(style.skin), 0, hy, 0, 1, 1.05, 1, 1);
  addHelmet(b, u, hy + 0.02);
  // Arms
  const sh: [number, number, number] = [0.14, ty + 0.1, 0];
  b.box(0.055, 0.22, 0.06, cloth(tunic, CLOTH.sleeve, Part.ArmR, sh), 0.14, ty - 0.005, 0.02);
  b.sphere(0.03, T(style.skin, 0, Part.ArmR, sh), 0.14, ty - 0.12, 0.04);
  b.box(0.055, 0.22, 0.06, cloth(tunic, CLOTH.sleeve, Part.ArmL, [-0.14, ty + 0.1, 0]), -0.14, ty - 0.005, 0.02);
  addWeapon(b, u, 0.14, ty - 0.12, 0.04, sh);
  if (u.shield) {
    b.cyl(0.11, 0.11, 0.02, 10, T(0xffffff, 1, Part.ArmL, [-0.14, ty + 0.1, 0]), -0.19, ty - 0.03, 0.06, 0, 0, Math.PI / 2);
    b.cyl(0.03, 0.03, 0.03, 6, T(STEEL, 0, Part.ArmL, [-0.14, ty + 0.1, 0]), -0.205, ty - 0.03, 0.06, 0, 0, Math.PI / 2);
  }
  if (villager) {
    // Basket on back
    b.cyl(0.07, 0.05, 0.12, 7, T(0x9a7a4a), 0, ty + 0.02, -0.11);
  }
}

/** A monk: long hooded robe with a team-coloured stole, rope belt and a tall staff. */
function monk(b: ModelBuilder, style: UnitStyle): void {
  const robe = 0x6a4a32;
  const robeDark = 0x56391f;
  // Feet show under the hem as the monk walks.
  b.box(0.07, 0.05, 0.11, T(LEATHER, 0, Part.LegL, [-0.05, 0.3, 0]), -0.05, 0.025, 0.02);
  b.box(0.07, 0.05, 0.11, T(LEATHER, 0, Part.LegR, [0.05, 0.3, 0]), 0.05, 0.025, 0.02);
  // Robe: flared skirt and body.
  b.cyl(0.12, 0.17, 0.4, 10, T(robe), 0, 0.24, 0);
  b.cyl(0.115, 0.12, 0.26, 10, T(robe), 0, 0.56, 0);
  b.cyl(0.125, 0.125, 0.025, 10, T(0xc8b07a), 0, 0.44, 0);
  // Stole in team colour down the front.
  b.box(0.09, 0.42, 0.02, T(0xffffff, 1), 0, 0.48, 0.12);
  // Head and hood.
  b.sphere(0.06, T(style.skin), 0, 0.77, 0.01, 1, 1.05, 1, 1);
  b.sphere(0.085, T(robeDark), 0, 0.79, -0.015, 1, 1.05, 1.05, 1);
  b.box(0.17, 0.05, 0.12, T(robeDark), 0, 0.68, -0.02);
  // Sleeves; the right hand holds the staff and lifts it while healing.
  const sh: [number, number, number] = [0.13, 0.66, 0];
  b.box(0.07, 0.22, 0.08, T(robe, 0, Part.ArmR, sh), 0.14, 0.56, 0.03);
  b.sphere(0.03, T(style.skin, 0, Part.ArmR, sh), 0.14, 0.44, 0.06);
  b.cyl(0.015, 0.015, 0.95, 6, T(WOOD, 0, Part.ArmR, sh), 0.15, 0.55, 0.08);
  b.sphere(0.035, T(0xd8b040, 0, Part.ArmR, sh), 0.15, 1.03, 0.08);
  b.box(0.07, 0.22, 0.08, T(robe, 0, Part.ArmL, [-0.13, 0.66, 0]), -0.13, 0.56, 0.05, 0.5, 0, 0);
}

function horse(b: ModelBuilder, color: number, armoured: boolean, accent: number): number {
  const y = 0.42;
  b.box(0.2, 0.2, 0.56, T(color), 0, y, 0);
  b.sphere(0.11, T(color), 0, y + 0.02, 0.22, 1, 1, 1.2, 1);
  b.box(0.1, 0.24, 0.12, T(color), 0, y + 0.18, 0.3, 0.55, 0, 0);
  b.box(0.08, 0.08, 0.2, T(color), 0, y + 0.29, 0.4, 0.2, 0, 0);
  b.box(0.03, 0.18, 0.06, T(0x1a1410), 0, y + 0.25, 0.27, 0.55, 0, 0);
  b.box(0.04, 0.18, 0.04, T(0x1a1410, 0, Part.Sway, [0, y + 0.05, -0.28]), 0, y - 0.04, -0.31, -0.35, 0, 0);
  const legs: [Part, number, number][] = [[Part.HorseFL, -0.07, 0.2], [Part.HorseFR, 0.07, 0.2], [Part.HorseBL, -0.07, -0.2], [Part.HorseBR, 0.07, -0.2]];
  for (const [p, x, z] of legs) {
    b.box(0.055, 0.34, 0.06, T(color, 0, p, [x, y - 0.06, z]), x, y - 0.23, z);
    b.box(0.06, 0.04, 0.07, T(0x1a1410, 0, p, [x, y - 0.06, z]), x, 0.02, z);
  }
  // Saddle cloth / barding in team colour.
  b.box(0.23, 0.12, 0.3, T(armoured ? STEEL : accent, armoured ? 0 : 1), 0, y + 0.06, -0.02);
  b.box(0.235, 0.1, 0.34, T(0xffffff, 1), 0, y - 0.04, -0.02);
  return y + 0.1;
}

function elephant(b: ModelBuilder, style: UnitStyle): void {
  const g = 0x8a8680;
  b.sphere(0.36, T(g), 0, 0.72, 0, 0.9, 0.85, 1.25, 1);
  b.sphere(0.2, T(g), 0, 0.85, 0.42, 1, 1, 1, 1);
  b.cyl(0.07, 0.035, 0.55, 6, T(g, 0, Part.Sway, [0, 0.8, 0.55]), 0, 0.55, 0.6, 0.15, 0, 0);
  b.box(0.04, 0.24, 0.2, T(g), -0.2, 0.86, 0.36, 0, 0.3, 0);
  b.box(0.04, 0.24, 0.2, T(g), 0.2, 0.86, 0.36, 0, -0.3, 0);
  b.cone(0.025, 0.25, 5, T(0xf0ead8), -0.09, 0.7, 0.58, -1.3, 0, 0);
  b.cone(0.025, 0.25, 5, T(0xf0ead8), 0.09, 0.7, 0.58, -1.3, 0, 0);
  const legs: [Part, number, number][] = [[Part.HorseFL, -0.18, 0.25], [Part.HorseFR, 0.18, 0.25], [Part.HorseBL, -0.18, -0.25], [Part.HorseBR, 0.18, -0.25]];
  for (const [p, x, z] of legs) b.cyl(0.09, 0.08, 0.5, 7, T(g, 0, p, [x, 0.55, z]), x, 0.25, z);
  // Howdah
  b.box(0.42, 0.06, 0.5, T(0xffffff, 1), 0, 1.06, -0.05);
  b.box(0.36, 0.22, 0.4, T(0xb08a3a), 0, 1.2, -0.05);
  b.pyramid(0.44, 0.2, 0.5, T(style.accent), 0, 1.31, -0.05);
  b.sphere(0.06, T(style.skin), 0, 1.22, 0.32);
}

function siege(b: ModelBuilder, u: UnitDef): void {
  const wood = 0x7a5a32;
  const wheel = (x: number, z: number, r = 0.14) => b.cyl(r, r, 0.05, 10, T(0x4a3420, 0, Part.Wheel, [x, r, z]), x, r, z, 0, 0, Math.PI / 2);
  switch (u.model) {
    case 'ram':
      b.box(0.5, 0.06, 0.9, T(wood), 0, 0.22, 0);
      b.prism(0.6, 0.35, 0.95, T(0x6a4a2a), 0, 0.28, 0);
      b.box(0.62, 0.05, 0.97, T(0xffffff, 1), 0, 0.28, 0);
      b.cyl(0.07, 0.07, 1.0, 8, T(0x5a3a1a), 0, 0.3, 0.2, Math.PI / 2, 0, 0);
      b.cone(0.09, 0.14, 8, T(STEEL), 0, 0.3, 0.75, Math.PI / 2, 0, 0);
      for (const z of [-0.3, 0.3]) for (const x of [-0.27, 0.27]) wheel(x, z);
      break;
    case 'mangonel':
      b.box(0.45, 0.08, 0.7, T(wood), 0, 0.2, 0);
      b.box(0.06, 0.4, 0.06, T(wood), -0.15, 0.42, 0.1);
      b.box(0.06, 0.4, 0.06, T(wood), 0.15, 0.42, 0.1);
      b.box(0.36, 0.06, 0.06, T(wood), 0, 0.6, 0.1);
      b.box(0.05, 0.05, 0.75, T(0x5a3a1a, 0, Part.SiegeArm, [0, 0.32, -0.05]), 0, 0.32, 0.2, -0.3, 0, 0);
      b.sphere(0.08, T(0x6a5a4a, 0, Part.SiegeArm, [0, 0.32, -0.05]), 0, 0.42, 0.55);
      b.box(0.46, 0.04, 0.2, T(0xffffff, 1), 0, 0.26, -0.28);
      for (const z of [-0.25, 0.25]) for (const x of [-0.25, 0.25]) wheel(x, z, 0.12);
      break;
    case 'trebuchet':
      b.box(0.7, 0.08, 1.0, T(wood), 0, 0.12, 0);
      for (const x of [-0.25, 0.25]) {
        b.box(0.07, 1.2, 0.07, T(wood), x, 0.7, 0.15, 0.25, 0, 0);
        b.box(0.07, 1.2, 0.07, T(wood), x, 0.7, -0.15, -0.25, 0, 0);
      }
      b.box(0.55, 0.07, 0.07, T(wood), 0, 1.25, 0);
      b.box(0.06, 0.06, 1.6, T(0x5a3a1a, 0, Part.SiegeArm, [0, 1.25, 0]), 0, 1.25, 0.3, -0.6, 0, 0);
      b.box(0.26, 0.26, 0.26, T(0x4a4a4a, 0, Part.SiegeArm, [0, 1.25, 0]), 0, 1.05, -0.3);
      b.box(0.72, 0.05, 0.3, T(0xffffff, 1), 0, 0.19, -0.35);
      break;
    case 'cannon':
      b.cyl(0.07, 0.09, 0.8, 10, T(0x2a2a2e), 0, 0.3, 0.15, Math.PI / 2 - 0.12, 0, 0);
      b.box(0.2, 0.1, 0.55, T(wood), 0, 0.2, -0.15, 0.25, 0, 0);
      b.box(0.08, 0.06, 0.4, T(0xffffff, 1), 0, 0.24, -0.4, 0.4, 0, 0);
      wheel(-0.17, 0.0, 0.18);
      wheel(0.17, 0.0, 0.18);
      break;
    default:
      break;
  }
}

function ship(b: ModelBuilder, u: UnitDef): void {
  const hull = 0x6a4a2a;
  const big = u.key === 'frigate' ? 1.35 : u.key === 'caravelle' ? 1.2 : u.key === 'fishingShip' ? 0.75 : 1;
  const L = 1.4 * big;
  const W = 0.5 * big;
  b.box(W, 0.25 * big, L, T(hull), 0, 0.02, 0);
  b.cone(W / 2, 0.45 * big, 4, T(hull), 0, 0.02, L / 2 + 0.2 * big, Math.PI / 2, Math.PI / 4, 0);
  // Team stripe: wider and longer than the hull and sitting below its deck, so no faces coincide.
  b.box(W + 0.03, 0.05, L + 0.02, T(0xffffff, 1), 0, 0.09 * big, 0);
  b.box(W * 0.9, 0.2 * big, 0.3 * big, T(0x5a3a1a), 0, 0.25 * big, -L * 0.35);
  if (u.key === 'fishingShip') {
    b.cyl(0.02, 0.02, 0.8, 5, T(WOOD), 0, 0.5, 0.05);
    b.box(0.5, 0.45, 0.02, T(0xe8e0cc, 0, Part.Flag, [0, 0.8, 0.05]), 0, 0.55, 0.07);
    b.box(0.3, 0.02, 0.3, T(0x8a7a5a), 0.25, 0.16, -0.2);
    return;
  }
  if (u.key === 'galley') {
    for (let i = 0; i < 5; i++) {
      const z = -L / 2 + 0.25 + i * (L - 0.5) / 4;
      b.box(0.5, 0.02, 0.04, T(WOOD, 0, Part.Wheel, [W / 2, 0.1, z]), W / 2 + 0.2, 0.06, z, 0, 0, -0.3);
      b.box(0.5, 0.02, 0.04, T(WOOD, 0, Part.Wheel, [-W / 2, 0.1, z]), -W / 2 - 0.2, 0.06, z, 0, 0, 0.3);
    }
  }
  if (u.key === 'caravelle') {
    // Transport: a wide open hold with cargo stacked in it.
    b.box(W * 0.8, 0.06, L * 0.4, T(0x3a2412), 0, 0.15 * big, 0.08 * L);
    for (const [cx, cz] of [[-0.12, 0.02], [0.12, 0.1], [-0.05, 0.22]]) b.box(0.18, 0.16, 0.18, T(0x9a7a4a), cx, 0.25 * big, cz * L);
  }
  const masts = u.key === 'frigate' ? [0.35, -0.3] : u.key === 'caravelle' ? [0.32, -0.18] : [0.1];
  for (const mz of masts) {
    const z = mz * L;
    b.cyl(0.025 * big, 0.03 * big, 1.3 * big, 5, T(WOOD), 0, 0.75 * big, z);
    if (u.key === 'caravel' || u.key === 'caravelle') b.box(0.02, 0.7, 0.65, T(0xf0eadc, 0, Part.Flag, [0, 1.1, z]), 0, 0.85, z, 0.3, 0, 0);
    else {
      b.box(0.7 * big, 0.5 * big, 0.02, T(0xf0eadc, 0, Part.Flag, [0, 1.1 * big, z]), 0, 0.9 * big, z + 0.02);
      if (u.key === 'frigate') b.box(0.55 * big, 0.35 * big, 0.02, T(0xf0eadc, 0, Part.Flag, [0, 1.45 * big, z]), 0, 1.35 * big, z + 0.02);
    }
    b.box(0.2, 0.12, 0.01, T(0xffffff, 1, Part.Flag, [0, 1.45 * big, z]), 0.1, 1.42 * big + (u.key === 'frigate' ? 0.2 : 0), z);
  }
  if (u.key === 'frigate') {
    for (let i = 0; i < 4; i++) {
      const z = -L / 2 + 0.4 + i * 0.4;
      b.cyl(0.03, 0.03, 0.2, 6, T(0x1a1a1e), W / 2 + 0.05, 0.12, z, 0, 0, Math.PI / 2);
      b.cyl(0.03, 0.03, 0.2, 6, T(0x1a1a1e), -W / 2 - 0.05, 0.12, z, 0, 0, Math.PI / 2);
    }
  }
}

/** Build the merged geometry for a unit kind, styled for a civilisation. */
export function buildUnitGeometry(u: UnitDef, style: UnitStyle): THREE.BufferGeometry {
  const b = new ModelBuilder();
  switch (u.model) {
    case 'cavalry':
    case 'heavyCavalry':
    case 'horseArcher':
    case 'scout': {
      const heavy = u.model === 'heavyCavalry';
      const top = horse(b, HORSE[(u.key.length + u.trainTime) % HORSE.length], heavy, style.accent);
      humanoid(b, u, style, top, true);
      break;
    }
    case 'elephant':
      elephant(b, style);
      break;
    case 'ram':
    case 'mangonel':
    case 'trebuchet':
    case 'cannon':
      siege(b, u);
      if (u.model !== 'ram') {
        // Crew member
        const crew: UnitDef = { ...u, weapon: 'none', helmet: 'cap', shield: false, model: 'infantry', classes: 0 };
        const g = new ModelBuilder();
        humanoid(g, crew, style, 0, false);
        b.merge(g, 0.32, 0, -0.35);
      }
      break;
    case 'ship':
      ship(b, u);
      break;
    case 'monk':
      monk(b, style);
      break;
    case 'engineer':
      // A soldier's build in the team colour, with a leather work apron and a tool bag at the hip.
      humanoid(b, u, style, 0, false);
      b.box(0.17, 0.2, 0.02, T(LEATHER), 0, 0.32, 0.075);
      b.box(0.07, 0.08, 0.06, T(0x6a4a2a), -0.13, 0.3, 0.02);
      b.cyl(0.008, 0.008, 0.1, 4, T(WOOD), -0.11, 0.37, 0.03);
      break;
    default:
      humanoid(b, u, style, 0, false);
  }
  return b.build();
}

export function unitScale(u: UnitDef): number {
  if (u.model === 'elephant') return 1.15;
  if (u.model === 'trebuchet') return 1.1;
  return 1.0;
}
